import { memo, type PropsWithChildren, useEffect, useState } from 'react'
import { motion, type TargetAndTransition, type Transition } from 'motion/react'
import type { RxDocument } from 'rxdb'

import type { Card } from '~/db/types'

import type { EditCardProps } from './edit-card/types'
import { EditCard } from './edit-card'

// TODO: Research why react compiler memoization failed in this case
// It shows that the component is memoized but it still causes component to re-render when the parent component re-renders
type CardWithAnimationProps = PropsWithChildren<{
  isActive: boolean
  isNew: boolean
}> &
  EditCardProps<RxDocument<Card>>

export const CARD_CREATION_ANIMATION_DURATION_SECONDS = 0.8

export const InnerCardWithAnimation = ({ card, isNew, isActive, ...props }: CardWithAnimationProps) => {
  // Parent is memoized so we need to use useState to track the latest card
  const [latestCard, setLatestCard] = useState<RxDocument<Card>>(card)
  useEffect(() => {
    const subscription = card.$.subscribe(setLatestCard)

    return () => subscription.unsubscribe()
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [])

  const animate: TargetAndTransition = isActive
    ? { rotate: [0, -6, 6, -4, 4, -2, 2, 0] }
    : { opacity: 1, y: 0, scale: 1, rotate: 0 }

  const transition: Transition = isActive
    ? { duration: CARD_CREATION_ANIMATION_DURATION_SECONDS, ease: 'easeOut' }
    : {
        duration: CARD_CREATION_ANIMATION_DURATION_SECONDS,
        type: 'spring',
        stiffness: 260,
        damping: 18,
      }

  return (
    <motion.div
      animate={animate}
      className="md:grow md:basis-2/5"
      exit={{ opacity: 0, x: -50 }}
      initial={isNew && !isActive && { opacity: 0, y: -25, scale: 0.95 }}
      transition={transition}
      layout
    >
      <EditCard card={latestCard} {...props} />
    </motion.div>
  )
}

const MemoizedInnerCardWithAnimation = memo(InnerCardWithAnimation)

MemoizedInnerCardWithAnimation.displayName = 'InnerCardWithAnimation'

export const CardWithAnimation = MemoizedInnerCardWithAnimation as typeof InnerCardWithAnimation
